import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, RefreshCw, XCircle } from "lucide-react";
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AuthToken } from '@/common/models/auth-token';
import { _authTokensSelectByQuery, authTokensReducerActions } from '@/common/reducers/DBServiceThunks';
import { useWebDispatch, useWebSelector } from '@/website/store/web-store';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TokenRevoker } from "./TokenRevoker";
import { backendApi } from '@/common/reducers/backend-api/backend-api';

export const AuthTokensTable: React.FC = () => {
  const dispatch = useWebDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState<Record<string, boolean>>({});
  
  // RTK Query mutation hooks
  const [refreshTokenMutation] = backendApi.endpoints.refreshToken.useMutation();
  
  // Select auth tokens from the store
  const tokens = useWebSelector(state => _authTokensSelectByQuery(state.DBAuthTokensReducer, {}, []));
  
  // Fetch tokens on component mount
  useEffect(() => {
    const fetchTokens = async () => {
      setIsLoading(true);
      try {
        await dispatch(authTokensReducerActions.find({}));
      } catch (error) {
        console.error('Error fetching auth tokens:', error);
        toast.error('Failed to load authentication tokens');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTokens();
  }, [dispatch]);

  const handleDeleteToken = async (tokenId: string) => {
    setIsDeleting(true);
    try {
      // Use the correct format for delete thunk
      await dispatch(authTokensReducerActions.delete({
        entity: { _id: tokenId } as Partial<AuthToken & { _id: string }>,
        options: {
          inCaseOfErrorRollbackOptimisticUpdateDisabled: false
        }
      }));
      toast.success("Token deleted", {
        description: "The authentication token has been successfully removed."
      });
    } catch (error) {
      console.error('Error deleting token:', error);
      toast.error("Error", {
        description: "Failed to delete the token. Please try again."
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleRefreshToken = async (tokenId: string) => {
    // Set refreshing state for this specific token
    setIsRefreshing(prev => ({ ...prev, [tokenId]: true }));
    
    try {
      const response = await refreshTokenMutation({ tokenId }).unwrap();
      
      if (response.success) {
        // Calculate the time gained in a readable format
        const timeGained = response.token?.timeGainedHours 
          ? `Gained ${Math.round(response.token.timeGainedHours)} hours` 
          : '';
          
        toast.success("Token refreshed", {
          description: `${response.message} ${timeGained}`
        });
        
        // Refresh the token list to show updated expiry
        await dispatch(authTokensReducerActions.find({}));
      } else {
        toast.info("Token not refreshed", {
          description: response.message
        });
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      toast.error("Refresh failed", {
        description: "Failed to refresh the token. Please try again."
      });
    } finally {
      // Clear refreshing state for this token
      setIsRefreshing(prev => ({ ...prev, [tokenId]: false }));
    }
  };

  // Format the date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Check if a token is expired
  const isTokenExpired = (expiryDate: string) => {
    try {
      return new Date(expiryDate) < new Date();
    } catch (e) {
      return true;
    }
  };

  // Get token status badge
  const getTokenStatusBadge = (expiryDate: string) => {
    const expired = isTokenExpired(expiryDate);
    
    if (expired) {
      return (
        <Badge variant="destructive">
          Expired
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="bg-green-500">
        Valid
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tokens || tokens.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No authentication tokens found.
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Test Token Revocation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Test Token Revocation</DialogTitle>
              <DialogDescription>
                This tool helps you test token revocation and refresh functionality.
              </DialogDescription>
            </DialogHeader>
            <TokenRevoker />
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableCaption>A list of your OAuth authentication tokens</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Data Access</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.map((token: AuthToken) => (
              <TableRow key={token._id}>
                <TableCell className="font-medium capitalize">
                  {token.tokenType}
                </TableCell>
                <TableCell>
                  {getTokenStatusBadge(token.expiresAt as string)}
                </TableCell>
                <TableCell>{formatDate(token.expiresAt as string)}</TableCell>
                <TableCell>
                  {token.additionalData?.dataAccessExpiresAt ? 
                    formatDate(token.additionalData.dataAccessExpiresAt) : 
                    'N/A'}
                </TableCell>
                <TableCell>{formatDate(token.createdAt || '')}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => token._id && handleRefreshToken(token._id.toString())}
                      disabled={isRefreshing[token._id?.toString() || '']}
                      title="Refresh Token"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      {isRefreshing[token._id?.toString() || ''] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Revoke Token"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Revoke Token: {token.tokenType}</DialogTitle>
                          <DialogDescription>
                            This will revoke access permissions for this token.
                          </DialogDescription>
                        </DialogHeader>
                        <TokenRevoker 
                          initialTokenType={
                            token.tokenType === 'facebook' || token.tokenType === 'instagram' 
                              ? token.tokenType 
                              : 'facebook'
                          } 
                        />
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => token._id && handleDeleteToken(token._id.toString())}
                      disabled={isDeleting}
                      title="Delete Token"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AuthTokensTable; 